// VIBRANIAM-SPACE v2 - ExportPanel.jsx
// Export settings live on the Uscita node, so this panel edits the graph
// directly - there is no second copy of the truth.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { formatBytes } from '../utils/particle-utils.js';

const PRESETS = [
  { label: '1080p60', width: 1920, height: 1080, fps: 60 },
  { label: '1080p25', width: 1920, height: 1080, fps: 25 },
  { label: '4K30',    width: 3840, height: 2160, fps: 30 },
  { label: 'Quadrato', width: 1080, height: 1080, fps: 30 },
  { label: 'Verticale', width: 1080, height: 1920, fps: 30 },
];

function Field({ label, children }) {
  return (
    <label className="mb-2 block">
      <span className="mb-1 block text-[9px] uppercase tracking-wider text-slate-500">{label}</span>
      {children}
    </label>
  );
}

const inputCls =
  'w-full rounded bg-ink-600 px-1.5 py-1 text-[11px] text-slate-200 outline-none focus:ring-1 focus:ring-vibe-cyan';

export default function ExportPanel({ graph, api, apiBase = '' }) {
  const outNode = graph.nodes.find((n) => n.type === 'r.output');
  const p = outNode?.params || {};

  const [audioFile, setAudioFile] = useState(null);
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [health, setHealth] = useState(null);
  const [vibraniamShown, setVibraniamShown] = useState(false);
  const pollRef = useRef(null);
  const vibraniamShownRef = useRef(false);

  // One-time-per-session reminder: an exported video that contains Image
  // Strobe or Mixer/Switcher should cite "vibraniam" (Christian Brogi's alias).
  const graphUsesStrobe = graph.nodes.some((n) => n.type === 'r.imageStrobe' || n.type === 'r.mixer');

  const set = (k, v) => outNode && api.setParam(outNode.id, k, v);

  // Il frontend e il backend condividono lo stesso origin sia in Electron
  // (main.cjs serve l'editor da http://127.0.0.1:<porta>) sia in dev (il proxy
  // Vite inoltra /api). Una base vuota significa quindi "stesso origin", non
  // "offline": e il probe di /api/health a decidere. Nel web statico /api/health
  // risponde 404 e si finisce correttamente offline.
  const effectiveApiBase = apiBase;

  // Detect if we are running in Electron (with Node integration)
  const isElectron = () => {
    return window.navigator.userAgent.includes('Electron') ||
           (window.process && window.process.versions && window.process.versions.node);
  };

  useEffect(() => {
    let cancelled = false;
    fetch(`${effectiveApiBase}/api/health`)
      .then((r) => r.json())
      .then((h) => { if (!cancelled) setHealth(h); })
      .catch(() => { if (!cancelled) setHealth({ offline: true }); });
    return () => { cancelled = true; };
  }, [effectiveApiBase]);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const poll = useCallback((jobId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await fetch(`${effectiveApiBase}/api/render/${jobId}`);
        const j = await r.json();
        setJob(j);
        if (j.status === 'done' || j.status === 'failed') {
          clearInterval(pollRef.current);
          setBusy(false);
          if (j.status === 'failed') setError(j.error || 'render fallito');
        }
      } catch (err) {
        clearInterval(pollRef.current);
        setBusy(false);
        setError(err.message);
      }
    }, 900);
  }, [effectiveApiBase]);

  const startRender = useCallback(async () => {
    // Fotosensibilita/attribuzione: ricorda una sola volta per sessione di
    // aggiungere il riferimento "vibraniam" quando il grafo usa lo strobo.
    if (graphUsesStrobe && !vibraniamShownRef.current) {
      vibraniamShownRef.current = true;
      setVibraniamShown(true);
    }
    setError(null); setBusy(true); setJob(null);
    try {
      const payload = { graph };
      if (audioFile) {
        const bytes = new Uint8Array(await audioFile.arrayBuffer());
        let binary = '';
        const chunk = 0x8000;
        for (let i = 0; i < bytes.length; i += chunk) {
          binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
        }
        payload.wavBase64 = btoa(binary);
      }
      const res = await fetch(`${effectiveApiBase}/api/render`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      setJob({ id: j.jobId, status: j.status, progress: 0, totalFrames: j.totalFrames });
      poll(j.jobId);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }, [graph, graphUsesStrobe, audioFile, effectiveApiBase, poll]);

  const cancelRender = useCallback(async () => {
    if (!job?.id) return;
    clearInterval(pollRef.current);
    await fetch(`${effectiveApiBase}/api/render/${job.id}`, { method: 'DELETE' }).catch(() => {});
    setJob(null); setBusy(false);
  }, [job?.id, effectiveApiBase]);

  const handleSaveVideo = useCallback(async () => {
    try {
      // Fetch the file as ArrayBuffer
      const res = await fetch(`${effectiveApiBase}/api/render/${job.id}/download`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const buffer = await res.arrayBuffer();

      // Ask user where to save
      const filePath = await window.electronAPI.saveDialog();
      if (!filePath) return; // user canceled

      // Write the file
      await window.electronAPI.writeFile(filePath, buffer);
      // Optionally show a success message
    } catch (err) {
      setError(err.message);
    }
  }, [effectiveApiBase, job?.id]);

  const totalFrames = Math.round((p.duration || 0) * (p.fps || 60));
  const pct = job?.progress ? Math.round(job.progress * 100) : 0;
  const distStatus = health?.offline ? 'offline'
    : health?.frontendBuilt === false ? 'build mancante'
    : health?.ffmpeg ? 'pronto' : 'ffmpeg assente';

  return (
    <div className="flex h-full flex-col overflow-y-auto border-l border-white/10 bg-ink-800/70 p-3">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-[11px] font-semibold uppercase tracking-widest text-slate-300">Export</h2>
        <span
          className={`rounded px-1.5 py-0.5 text-[9px] ${
            distStatus === 'pronto' ? 'bg-emerald-500/20 text-emerald-300'
              : distStatus === 'offline' ? 'bg-red-500/20 text-red-300'
              : 'bg-amber-500/20 text-amber-300'
          }`}
          title={health?.ffmpeg || 'backend non raggiungibile'}
        >
          {distStatus}
        </span>
      </div>

      {health?.frontendBuilt === false && (
        <div className="mb-3 rounded border border-amber-500/40 bg-amber-950/40 p-2 text-[10px] leading-snug text-amber-200">
          Il render headless usa la build del frontend. Lancia <code>npm run build</code> nella
          cartella del progetto prima di esportare.
        </div>
      )}

      <div className="mb-3 grid grid-cols-2 gap-1">
        {PRESETS.map((pr) => (
          <button
            key={pr.label}
            type="button"
            onClick={() => { set('width', pr.width); set('height', pr.height); set('fps', pr.fps); }}
            className={`rounded px-1.5 py-1 text-[9px] transition ${
              p.width === pr.width && p.height === pr.height && p.fps === pr.fps
                ? 'bg-vibe-cyan text-ink-900' : 'bg-ink-600 text-slate-300 hover:bg-ink-500'
            }`}
          >
            {pr.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-x-2">
        <Field label="Larghezza">
          <input type="number" min={256} max={3840} step={2} value={p.width ?? 1920}
            onChange={(e) => set('width', Number(e.target.value))} className={inputCls} />
        </Field>
        <Field label="Altezza">
          <input type="number" min={256} max={2160} step={2} value={p.height ?? 1080}
            onChange={(e) => set('height', Number(e.target.value))} className={inputCls} />
        </Field>
        <Field label="FPS">
          <select value={p.fps ?? 60} onChange={(e) => set('fps', Number(e.target.value))} className={inputCls}>
            {[24, 25, 30, 50, 60].map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </Field>
        <Field label="Durata (s)">
          <input type="number" min={1} max={600} step={1} value={p.duration ?? 12}
            onChange={(e) => set('duration', Number(e.target.value))} className={inputCls} />
        </Field>
      </div>

      <Field label={`Qualita — CRF ${p.crf ?? 16} (piu basso = migliore)`}>
        <input type="range" min={8} max={28} step={1} value={p.crf ?? 16}
          onChange={(e) => set('crf', Number(e.target.value))} className="w-full" />
      </Field>

      <div className="grid grid-cols-2 gap-x-2">
        <Field label="Formato">
          <select value={p.format ?? 'mp4'} onChange={(e) => set('format', e.target.value)} className={inputCls}>
            <option value="mp4">MP4 (H.264)</option>
            <option value="webm">WebM (VP9)</option>
          </select>
        </Field>
        <Field label="Stereo 3D">
          <select value={p.stereo ?? 'off'} onChange={(e) => set('stereo', e.target.value)} className={inputCls}>
            <option value="off">Off</option>
            <option value="anaglyph">Anaglifo R/C</option>
            <option value="sbs">Side-by-Side</option>
            <option value="wiggle">Wiggle 3D</option>
          </select>
        </Field>
      </div>

      {(p.stereo ?? 'off') !== 'off' && (
        <Field label={`Intensita stereo — ${Math.round((p.stereoStrength ?? 0.7) * 100)}%`}>
          <input type="range" min={0} max={1} step={0.01} value={p.stereoStrength ?? 0.7}
            onChange={(e) => set('stereoStrength', Number(e.target.value))} className="w-full" />
        </Field>
      )}

      <Field label="Audio export (WAV, sincronizzato)">
        {/* accept e solo un suggerimento: il selettore di sistema lascia comunque
            scegliere "tutti i file", ed e cosi che un .flac finiva al decoder WAV.
            Meglio dirlo subito che dopo aver caricato decine di MB. */}
        <input type="file" accept=".wav,audio/wav,audio/x-wav,audio/wave"
          onChange={(e) => {
            const f = e.target.files?.[0] || null;
            if (f && !/\.wave?$/i.test(f.name)) {
              setError(`"${f.name}" non e un WAV. Il decoder accetta solo WAV PCM o IEEE float: converti il file (es. con ffmpeg) e riprova.`);
              setAudioFile(null);
              e.target.value = '';
              return;
            }
            setError(null);
            setAudioFile(f);
          }}
          className="w-full text-[10px] text-slate-400 file:mr-2 file:rounded file:border-0 file:bg-ink-600 file:px-2 file:py-1 file:text-[10px] file:text-slate-200 hover:file:bg-ink-500" />
      </Field>
      {audioFile && (
        <p className="-mt-1 mb-2 text-[9px] text-slate-500">
          {audioFile.name} · {formatBytes(audioFile.size)} — analizzato lato server, un frame di
          feature per ogni frame video, poi muxato nell&apos;MP4.
        </p>
      )}

      <div className="mb-3 rounded border border-white/5 bg-ink-700/60 p-2 text-[10px] text-slate-400">
        <div className="flex justify-between"><span>Frame totali</span>
          <span className="font-mono text-slate-200">{totalFrames}</span></div>
        <div className="flex justify-between"><span>Nodi / cavi</span>
          <span className="font-mono text-slate-200">{graph.nodes.length} / {graph.edges.length}</span></div>
        <div className="flex justify-between"><span>Modulazioni</span>
          <span className="font-mono text-slate-200">{api.modCount}</span></div>
      </div>

      {health?.offline && !isElectron() ? (
        <div className="mb-2 rounded border border-white/10 bg-ink-700/60 p-2.5 text-[10px] leading-relaxed text-slate-400">
          <div className="mb-1 font-semibold text-slate-300">Export non disponibile qui</div>
          Generare l&apos;MP4 richiede il backend locale (Node + FFmpeg), che una pagina web statica
          non puo eseguire. Il viewport, il patcher e tutte le modulazioni funzionano normalmente.
          <p className="mt-2 text-slate-300">
            Scarica l&apos;app per desktop con la possibilita di esportare fino a 4K il rendering
            su file, con i tuoi audio e il video realizzato da VIBRANIAM-SPACE.
          </p>
          <a
            href="https://drive.google.com/file/d/1Z2Mc8Imtf12YWoHcMMfk7PRGOEXguvYi/view?usp=sharing"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 block rounded bg-vibe-lime px-3 py-1.5 text-center text-[11px] font-semibold text-ink-900 hover:brightness-110">
            Scarica l&apos;app desktop (Windows, 92 MB)
          </a>
          <div className="mt-1.5 text-[9px] text-slate-500">
            Versione portable 0.1.0: un solo file, nessuna installazione. In alternativa,
            per lavorare dai sorgenti: <code>npm run dev</code>.
          </div>
        </div>
      ) : !busy ? (
        <button type="button" onClick={startRender}
          className="mb-2 w-full rounded bg-vibe-cyan px-3 py-2 text-[11px] font-semibold text-ink-900 transition hover:brightness-110">
          Esporta {(p.format ?? 'mp4') === 'webm' ? 'WebM' : 'MP4'}
        </button>
      ) : (
        <button type="button" onClick={cancelRender}
          className="mb-2 w-full rounded border border-red-500/40 px-3 py-2 text-[11px] font-semibold text-red-300 transition hover:bg-red-500/10">
          Annulla render
        </button>
      )}

      {/* Fra il click e la creazione del job il server decodifica e analizza
          l'audio: con un file lungo puo volerci parecchio, e senza questo
          riquadro la UI resta muta e sembra piantata. */}
      {busy && !job && (
        <div className="mb-2 rounded border border-white/5 bg-ink-700/60 p-2 text-[10px] text-slate-400">
          {audioFile ? 'Analisi audio in corso...' : 'Preparazione del render...'}
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded bg-ink-900">
            <div className="h-full w-1/3 animate-pulse bg-vibe-cyan/70" />
          </div>
        </div>
      )}

      {job && (
        <div className="mb-2 rounded border border-white/5 bg-ink-700/60 p-2">
          <div className="mb-1 flex justify-between text-[10px] text-slate-400">
            <span>{job.status}</span>
            <span className="font-mono">{job.renderedFrames ?? 0}/{job.totalFrames ?? totalFrames} · {pct}%</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded bg-ink-900">
            <div className="h-full bg-vibe-cyan transition-all" style={{ width: `${pct}%` }} />
          </div>
          {job.status === 'done' && (
            <>
              {isElectron() ? (
                <button type="button" onClick={handleSaveVideo}
                  className="mt-2 block rounded bg-vibe-lime px-3 py-1.5 text-center text-[11px] font-semibold text-ink-900 hover:brightness-110">
                  Salva video
                </button>
              ) : (
                <a href={`${effectiveApiBase}/api/render/${job.id}/download`}
                  className="mt-2 block rounded bg-vibe-lime px-3 py-1.5 text-center text-[11px] font-semibold text-ink-900 hover:brightness-110">
                  Scarica {(p.format ?? 'mp4') === 'webm' ? 'WebM' : 'MP4'} {job.bytes ? `(${formatBytes(job.bytes)})` : ''}
                </a>
              )}
              {/* Il file esiste gia su disco: senza dirlo, l'unico modo di
                  trovarlo era indovinare. */}
              {health?.outputDir && (
                <div className="mt-1.5 break-all text-[9px] leading-relaxed text-slate-500">
                  Salvato in <span className="font-mono text-slate-400">{health.outputDir}</span>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {error && (
        <div className="rounded border border-red-500/40 bg-red-950/40 p-2 text-[10px] text-red-200">{error}</div>
      )}

      {vibraniamShown && (
        <div className="rounded border border-vibe-cyan/40 bg-ink-700/70 p-2 text-[10px] leading-snug text-slate-300">
          <div className="mb-1 font-semibold text-vibe-cyan">Ricorda il riferimento</div>
          Questo video usa Image Strobe o Mixer/Switcher: aggiungi il riferimento a
          <span className="font-semibold text-slate-100"> vibraniam</span> quando pubblichi il risultato.
          <button type="button" onClick={() => setVibraniamShown(false)}
            className="mt-1.5 block w-full rounded bg-ink-600 px-2 py-1 text-[10px] text-slate-200 hover:bg-ink-500">
            Ho capito
          </button>
        </div>
      )}

      <p className="mt-auto pt-3 text-[9px] leading-relaxed text-slate-600">
        Il render headless carica la stessa pagina del viewport e avanza di 1/fps per frame: fra
        anteprima ed export cambia solo la risoluzione, mai il numero di particelle o i semi casuali.
      </p>
    </div>
  );
}